import { ContentArea } from '../types';
import { isFiberNode } from './nodes';

interface IAddTagToSubrangeProps {
  subRange: Range;
  contentArea: ContentArea;
  idAndAriaExtension: string;
  id?: string;
}

interface ISurroundTextNodesInRangeProps {
  range: Range;
  contentArea: ContentArea;
}

/**
 * Surrounds subRange with an HTML element (mark/anchor).
 *
 * @interface IAddTagToSubrangeProps
 * @property {Range} subRange - The range containing the text that should be wrapped.
 * @property {ContentArea} contentArea - The `ContentArea` object the subRange was created from.
 * @property {string} idAndAriaExtension - Extension for HTML attributes added to the HTML element that wraps the `Range` object (unique for each subRange).
 * @property {string} id - The base ID, which is the same for each subRange in the range.
 * @returns {void}
 */
const addTagToSubrange = ({
  subRange,
  contentArea,
  idAndAriaExtension,
  id,
}: IAddTagToSubrangeProps): void => {
  const newParent = document.createElement(contentArea.tagName || 'mark');
  if (id) {
    newParent.setAttribute(
      'id',
      `${
        contentArea.idPrefix ? `${contentArea.idPrefix}-` : ''
      }${id}-${idAndAriaExtension}`,
    );
  }

  if (contentArea.classNames?.length) {
    contentArea.classNames?.forEach((className) => {
      newParent.classList.add(className);
    });
  }

  if (contentArea.tagName === 'a') {
    newParent.setAttribute('href', contentArea.href || '');
    if (contentArea.target) {
      newParent.setAttribute('target', contentArea.target);
    }
    if (contentArea.rel) {
      newParent.setAttribute('rel', contentArea.rel);
    }
  }

  subRange.surroundContents(newParent);

  if (contentArea.clickableSpan) {
    const clickableSpan = document.createElement('span');
    clickableSpan.role = 'button';
    clickableSpan.tabIndex = 0;
    subRange.surroundContents(clickableSpan);
  }
};

/**
 * Surrounds a complete HTML `Node` object with a `Range` object.
 *
 * @param {Node} node - The `Node` object to surround.
 * @returns {Range} The `Range` object surrounding the `Node` object.
 */
const createSubRange = (node: Node): Range => {
  const subRange = document.createRange();
  subRange.selectNodeContents(node);

  return subRange;
};

/**
 * Finds all text nodes a `Range` object contains.
 *
 * @param {Node} rootNode - The node that contains the start and end points of the range.
 * @param {Range} range - The range from which the text nodes shall be extracted.
 * @returns {Node[]} An array of the text nodes surrounded by the range.
 */
const findAllTextNodesInRange = (rootNode: Node, range: Range): Node[] => {
  if (!rootNode.hasChildNodes()) {
    if (rootNode.nodeType !== Node.TEXT_NODE || rootNode.textContent === '') {
      return [];
    }

    return [rootNode];
  }

  const childNodes = Array.from(rootNode.childNodes).filter(
    (node) => !isFiberNode(node),
  );
  let textNodes: Node[] = [];

  childNodes.forEach((child) => {
    // save all text nodes that are part of the range and are not empty
    if (range.intersectsNode(child)) {
      if (child.nodeType === Node.TEXT_NODE && child.textContent !== '') {
        textNodes = [...textNodes, child];
      } else {
        textNodes = [...textNodes, ...findAllTextNodesInRange(child, range)];
      }
    }
  });

  return textNodes;
};

/**
 * A range might partially select nodes, which is not a problem for nodes of type `TEXT_NODE` but will cause issues for nodes of type `ELEMENT_NODE` (graphic elements).
 * Therefore, the range is broken down into subranges {@link findAllTextNodesInRange}, each containing a partial or full text node.
 * These subranges can then be easily wrapped inside a new HTML node of type `ELEMENT_NODE` {@link createSubRange}, {@link addTagToSubrange}.
 *
 * @param {Range} range - The range containing the text nodes to wrap.
 * @param {ContentArea} contentArea - The `ContentArea` object the range is based on.
 * @returns {void}
 */
const surroundTextNodesInRange = ({
  range,
  contentArea,
}: ISurroundTextNodesInRangeProps): void => {
  // find all children in the range
  const childrenInRange = findAllTextNodesInRange(
    range.commonAncestorContainer as Element,
    range,
  );

  // add a unique mark tag to each of the children
  // start and end container might be partially selected (offset needs to be set)
  childrenInRange.forEach((child, index) => {
    const idAndAriaExtension = `${index + 1}/${childrenInRange.length}`;
    const subRange = createSubRange(child);

    if (subRange.endOffset < 1) {
      return;
    }

    if (child === range.startContainer) {
      subRange.setStart(range.startContainer, range.startOffset);
    }

    if (child === range.endContainer) {
      subRange.setEnd(range.endContainer, range.endOffset);
    }

    const id = contentArea?.id;

    addTagToSubrange({
      subRange,
      contentArea,
      idAndAriaExtension,
      id,
    });
  });
};

export default surroundTextNodesInRange;
